import type { RespiratoryMusclePatientContext, RespiratoryMuscleProfessionalContext, RespiratoryTestPosition } from './respiratoryMuscleDomain';
import { getAssessmentInterpretationOverride } from '../utils/interpretationOverride';

export interface VitalCapacityAttempt { liters: number; acceptable: boolean; note?: string | null }
export interface VitalCapacityPositionResult { attempts: VitalCapacityAttempt[]; notPerformedReason?: string | null; qualityNotes?: string | null }
export interface PosturalVitalCapacityResultPayload {
  report?: { interpretation?: string; [key: string]: unknown };
  version: 1;
  maneuver: 'slow' | 'forced';
  seated: VitalCapacityPositionResult;
  supine: VitalCapacityPositionResult;
  interface: 'mouthpiece' | 'mask' | 'other';
  device?: string | null;
  orthopnea: boolean;
  lipLeak: boolean;
  cooperationLimited: boolean;
  abdominalSupport: boolean;
  notes?: string | null;
  patientContext?: RespiratoryMusclePatientContext | null;
  professionalContext?: RespiratoryMuscleProfessionalContext | null;
  finishedAt?: string | null;
}
export interface SnipAttempt { pressure: number; nostril: 'left' | 'right'; acceptable: boolean; note?: string | null }
export interface SnipResultPayload {
  report?: { interpretation?: string; [key: string]: unknown };
  version: 1;
  attempts: SnipAttempt[];
  position: RespiratoryTestPosition;
  angleDegrees?: number | null;
  otherPosition?: string | null;
  device?: string | null;
  initialVolume: 'FRC' | 'other';
  contralateralNostril: 'open' | 'occluded';
  mouthClosed: boolean;
  nasalObstruction: boolean;
  bulbarImpairment: boolean;
  cooperationLimited: boolean;
  notes?: string | null;
  patientContext?: RespiratoryMusclePatientContext | null;
  professionalContext?: RespiratoryMuscleProfessionalContext | null;
  finishedAt?: string | null;
}
type Dict = Record<string, unknown>;
const record = (x: unknown): Dict => x && typeof x === 'object' && !Array.isArray(x) ? x as Dict : {};
const finite = (x: unknown): number | null => {
  if (x == null || typeof x === 'boolean' || (typeof x === 'string' && !x.trim())) return null;
  const n = typeof x === 'number' ? x : typeof x === 'string' ? Number(x.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : null;
};
const clean = (x: unknown) => typeof x === 'string' && x.trim() ? x.trim() : null;
const positionResult = (x: unknown): VitalCapacityPositionResult => {
  const r = record(x);
  return { attempts: Array.isArray(r.attempts) ? r.attempts.flatMap((item) => {
    const a = record(item), liters = finite(typeof item === 'number' ? item : a.liters);
    return liters != null && liters > 0 ? [{ liters, acceptable: a.acceptable !== false, note: clean(a.note) }] : [];
  }) : [], notPerformedReason: clean(r.notPerformedReason), qualityNotes: clean(r.qualityNotes) };
};
export const normalizePosturalVitalCapacityPayload = (input: unknown): PosturalVitalCapacityResultPayload => {
  const p = record(input);
  return { version: 1, report: p.report && typeof p.report === 'object' ? p.report as PosturalVitalCapacityResultPayload['report'] : undefined, maneuver: p.maneuver === 'forced' ? 'forced' : 'slow', seated: positionResult(p.seated), supine: positionResult(p.supine),
    interface: p.interface === 'mask' || p.interface === 'other' ? p.interface : 'mouthpiece',
    device: clean(p.device), orthopnea: p.orthopnea === true, lipLeak: p.lipLeak === true,
    cooperationLimited: p.cooperationLimited === true, abdominalSupport: p.abdominalSupport === true,
    notes: clean(p.notes), patientContext: p.patientContext as RespiratoryMusclePatientContext | undefined,
    professionalContext: p.professionalContext as RespiratoryMuscleProfessionalContext | undefined, finishedAt: clean(p.finishedAt) };
};
const bestCapacity = (p: VitalCapacityPositionResult): number | null => {
  const values = p.notPerformedReason ? [] : p.attempts.filter((item) => item.acceptable).map((item) => item.liters);
  return values.length ? Math.max(...values) : null;
};
export const summarizePosturalVitalCapacityPayload = (input: unknown) => {
  const p = normalizePosturalVitalCapacityPayload(input);
  const bestSeated = bestCapacity(p.seated), bestSupine = bestCapacity(p.supine);
  const reductionPercent = bestSeated != null && bestSeated > 0 && bestSupine != null ? 100 * (bestSeated - bestSupine) / bestSeated : null;
  const qualityLimited = p.lipLeak || p.cooperationLimited || [p.seated,p.supine].some((pos) => !pos.notPerformedReason && pos.attempts.filter((a) => a.acceptable).length < 3);
  const pediatric = p.patientContext?.ageYears != null && p.patientContext.ageYears < 18;
  const aboveStudyThreshold = reductionPercent != null && reductionPercent > 25;
  const severity = reductionPercent == null ? 'Comparação incompleta' : aboveStudyThreshold ? 'Queda postural acentuada' : 'Comparação postural descritiva';
  const automatic = reductionPercent == null
    ? 'Não há medidas aceitáveis nas duas posições para calcular a variação postural.'
    : `A ${p.maneuver === 'forced' ? 'CVF' : 'CV lenta'} foi de ${bestSeated!.toFixed(2)} L sentado e ${bestSupine!.toFixed(2)} L em supino, com ${reductionPercent >= 0 ? 'redução' : 'aumento'} de ${Math.abs(reductionPercent).toFixed(1)}%. ${aboveStudyThreshold ? 'Queda superior a 25% foi associada a comprometimento diafragmático em uma pequena amostra neuromuscular (Fromageot, 2001); requer correlação clínica e confirmação.' : 'Este resultado, isoladamente, não exclui comprometimento diafragmático.'}`;
  const caveats = [qualityLimited ? 'Qualidade limitada ou menos de três manobras aceitáveis em uma das posições; considerar repetição.' : null,
    pediatric ? 'Não aplicar automaticamente pontos de corte de adultos à população pediátrica.' : null,
    'A variação depende do diagnóstico, da técnica, da interface e do suporte abdominal; não representa diagnóstico isolado.'].filter((v): v is string => Boolean(v));
  return { bestSeated, bestSupine, reductionPercent, qualityLimited, aboveStudyThreshold, severity,
    tone: aboveStudyThreshold ? 'warn' as const : 'neutral' as const,
    interpretation: getAssessmentInterpretationOverride(input) || automatic, caveats,
    reference: 'Fromageot et al., 2001; ERS, 2019', referenceUrl: 'https://pubmed.ncbi.nlm.nih.gov/11239298/' };
};
export const normalizeSnipPayload = (input: unknown): SnipResultPayload => {
  const p = record(input);
  const positions: RespiratoryTestPosition[] = ['seated','supine','semi_recumbent','other','unknown'];
  return { version: 1, report: p.report && typeof p.report === 'object' ? p.report as SnipResultPayload['report'] : undefined, attempts: Array.isArray(p.attempts) ? p.attempts.flatMap((item) => {
    const a = record(item), pressure = finite(a.pressure);
    return pressure != null && pressure !== 0 ? [{ pressure, nostril: a.nostril === 'right' ? 'right' as const : 'left' as const, acceptable: a.acceptable !== false, note: clean(a.note) }] : [];
  }) : [], position: positions.includes(p.position as RespiratoryTestPosition) ? p.position as RespiratoryTestPosition : 'seated',
    angleDegrees: finite(p.angleDegrees), otherPosition: clean(p.otherPosition), device: clean(p.device), initialVolume: p.initialVolume === 'other' ? 'other' : 'FRC',
    contralateralNostril: p.contralateralNostril === 'occluded' ? 'occluded' : 'open', mouthClosed: p.mouthClosed !== false,
    nasalObstruction: p.nasalObstruction === true, bulbarImpairment: p.bulbarImpairment === true, cooperationLimited: p.cooperationLimited === true,
    notes: clean(p.notes), patientContext: p.patientContext as RespiratoryMusclePatientContext | undefined,
    professionalContext: p.professionalContext as RespiratoryMuscleProfessionalContext | undefined, finishedAt: clean(p.finishedAt) };
};
export const summarizeSnipPayload = (input: unknown) => {
  const p = normalizeSnipPayload(input);
  const valid = p.attempts.filter((a) => a.acceptable), values = valid.map((a) => Math.abs(a.pressure));
  const max = values.length ? Math.max(...values) : null;
  const best = (side: 'left'|'right') => { const v = valid.filter((a) => a.nostril === side).map((a) => Math.abs(a.pressure)); return v.length ? Math.max(...v) : null; };
  const qualityLimited = p.nasalObstruction || p.bulbarImpairment || p.cooperationLimited || !p.mouthClosed || p.initialVolume !== 'FRC' || p.contralateralNostril !== 'open' || valid.length < 10;
  const automatic = max == null ? 'Não há manobra aceitável de SNIP para interpretar.'
    : `A maior magnitude da pressão inspiratória nasal foi de ${max.toFixed(1)} cmH2O. O SNIP complementa a PImax na avaliação da força inspiratória; valores baixos também podem refletir técnica, esforço ou limitações nasais/bulbares. Não foi aplicada classificação normativa automática.`;
  return { max, bestLeft: best('left'), bestRight: best('right'), validAttempts: valid.length, qualityLimited,
    severity: max == null ? 'Sem medida aceitável' : qualityLimited ? 'Interpretar com ressalvas técnicas' : 'Resultado descritivo',
    tone: qualityLimited ? 'warn' as const : 'neutral' as const,
    interpretation: getAssessmentInterpretationOverride(input) || automatic,
    caveats: [valid.length < 10 ? 'Menos de dez manobras aceitáveis: repetir até obter esforços máximos consistentes quando tolerado.' : null,
      p.nasalObstruction ? 'Obstrução nasal pode reduzir a pressão medida.' : null,
      p.bulbarImpairment ? 'Comprometimento bulbar pode limitar a execução e a transmissão de pressão.' : null,
      'SNIP e PImax são complementares; seus valores não são intercambiáveis.'].filter((v): v is string => Boolean(v)),
    reference: 'ERS, 2019; ARTP, 2020', referenceUrl: 'https://doi.org/10.1136/bmjresp-2020-000575' };
};
